import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const ORANGE = [249, 115, 22];
const SLATE_DARK = [15, 23, 42];
const SLATE_LIGHT = [100, 116, 139];
const BORDER = [226, 232, 240];

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR"); } catch { return iso; }
}

function fmtMinutes(m) {
  if (!m && m !== 0) return "—";
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return h === 0 ? `${mm}min` : `${h}h ${mm}min`;
}

export function generateOSPdf(os, companyName = "Induscalta Morro Azul — PCM") {
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const w = pdf.internal.pageSize.getWidth();
  const marginX = 40;
  let y = 40;

  // Header band
  pdf.setFillColor(...SLATE_DARK);
  pdf.rect(0, 0, w, 80, "F");
  pdf.setFillColor(...ORANGE);
  pdf.rect(0, 80, w, 4, "F");

  pdf.setTextColor(...ORANGE);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  pdf.text("ORDEM DE SERVIÇO — PCM", marginX, 32);

  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(22);
  pdf.text(`OS #${os.number}`, marginX, 60);

  pdf.setFontSize(9);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(200, 210, 220);
  pdf.text(companyName, w - marginX, 32, { align: "right" });
  pdf.text(`Emitido em ${fmtDate(new Date().toISOString())}`, w - marginX, 46, { align: "right" });

  y = 110;

  // Status + priority chips
  const statusColors = {
    "Aberta": [220, 38, 38],
    "Em Andamento": [234, 179, 8],
    "Concluída": [22, 163, 74],
    "Cancelada": [100, 116, 139],
  };
  const drawChip = (x, label, color) => {
    pdf.setFillColor(...color);
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    const tw = pdf.getTextWidth(label) + 14;
    pdf.rect(x, y - 12, tw, 18, "F");
    pdf.text(label, x + 7, y + 1);
    return tw;
  };
  let cx = marginX;
  cx += drawChip(cx, os.status, statusColors[os.status] || SLATE_LIGHT) + 6;
  drawChip(cx, `Prioridade ${os.priority}`, ORANGE);
  y += 24;

  // Info grid
  pdf.setTextColor(...SLATE_DARK);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);

  const rows = [
    ["Equipamento", `${os.equipment_name} [${os.equipment_tag}]`, "Setor", os.sector],
    ["Disciplina", os.discipline, "Tipo", os.maintenance_type],
    ["Solicitante", os.requester_name || "—", "Técnico(s)", (os.technician_names && os.technician_names.length ? os.technician_names.join(", ") : (os.technician_name || "Pendente"))],
    ["Abertura", fmtDate(os.created_at), "Fechamento", os.closed_at ? fmtDate(os.closed_at) : "—"],
    ["Tempo Parado", fmtMinutes(os.downtime_minutes), "Atualizado", fmtDate(os.updated_at)],
  ];
  autoTable(pdf, {
    startY: y,
    body: rows.map((r) => [
      { content: r[0], styles: { fontStyle: "bold", textColor: SLATE_LIGHT, fontSize: 8 } },
      { content: r[1], styles: { textColor: SLATE_DARK } },
      { content: r[2], styles: { fontStyle: "bold", textColor: SLATE_LIGHT, fontSize: 8 } },
      { content: r[3], styles: { textColor: SLATE_DARK } },
    ]),
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 6, lineColor: BORDER },
    margin: { left: marginX, right: marginX },
    columnStyles: { 0: { cellWidth: 90 }, 1: { cellWidth: 170 }, 2: { cellWidth: 90 } },
  });
  y = pdf.lastAutoTable.finalY + 18;

  // Section helper
  const sectionTitle = (title) => {
    pdf.setDrawColor(...ORANGE);
    pdf.setLineWidth(2);
    pdf.line(marginX, y, marginX + 20, y);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(...ORANGE);
    pdf.text(title.toUpperCase(), marginX + 26, y + 3);
    y += 12;
  };

  const paragraph = (text) => {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(...SLATE_DARK);
    const lines = pdf.splitTextToSize(text || "—", w - marginX * 2);
    pdf.text(lines, marginX, y + 4);
    y += lines.length * 12 + 8;
  };

  const ensureSpace = (min) => {
    if (y + min > pdf.internal.pageSize.getHeight() - 40) {
      pdf.addPage();
      y = 40;
    }
  };

  ensureSpace(80);
  sectionTitle("Descrição do Problema");
  paragraph(os.description);

  if (os.recommended_actions) {
    ensureSpace(60);
    sectionTitle("Ações Recomendadas");
    paragraph(os.recommended_actions);
  }
  if (os.executed_actions) {
    ensureSpace(60);
    sectionTitle("Ações Executadas");
    paragraph(os.executed_actions);
  }
  if (os.parts_used) {
    ensureSpace(60);
    sectionTitle("Peças / Materiais Utilizados");
    paragraph(os.parts_used);
  }
  if (os.parts_items && os.parts_items.length) {
    ensureSpace(60);
    sectionTitle("Peças Baixadas do Estoque");
    paragraph(os.parts_items.map((p) => `${p.code || "NÃO CADASTRADA"} — ${p.name}: ${p.quantity} ${p.unit}`).join("\n"));
  }

  // History timeline as table
  if (os.history && os.history.length) {
    ensureSpace(80);
    sectionTitle("Histórico");
    autoTable(pdf, {
      startY: y,
      head: [["Quando", "Por", "Ação", "Observação"]],
      body: os.history.map((h) => [fmtDate(h.at), h.by || "—", h.action, h.note || ""]),
      theme: "striped",
      headStyles: { fillColor: SLATE_DARK, textColor: 255, fontSize: 9 },
      styles: { fontSize: 8, cellPadding: 4, lineColor: BORDER },
      margin: { left: marginX, right: marginX },
    });
    y = pdf.lastAutoTable.finalY + 16;
  }

  // Signatures
  ensureSpace(80);
  sectionTitle("Assinaturas");
  y += 30;
  pdf.setDrawColor(...BORDER);
  pdf.setLineWidth(0.5);
  const colW = (w - marginX * 2 - 30) / 2;
  pdf.line(marginX, y, marginX + colW, y);
  pdf.line(marginX + colW + 30, y, w - marginX, y);
  pdf.setFontSize(9);
  pdf.setTextColor(...SLATE_LIGHT);
  pdf.text("Técnico Responsável", marginX, y + 12);
  pdf.text("Supervisor", marginX + colW + 30, y + 12);

  // Footer
  const pageCount = pdf.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor(...SLATE_LIGHT);
    pdf.text(`${companyName}  ·  OS #${os.number}  ·  Página ${i}/${pageCount}`, w / 2, pdf.internal.pageSize.getHeight() - 20, { align: "center" });
  }

  pdf.save(`OS-${os.number}-${os.equipment_tag}.pdf`);
}
